import _ from 'lodash';

/**
 * Service to use the Fullscreen API
 */
export default class FullscreenService {
	/**
	 * Detect browser prefix
	 */
	constructor() {
		// detect and store prefixes
		let specs = {
			// browser support as of 4/2016
			// see http://caniuse.com/#search=fullscr
			// see https://developer.mozilla.org/en-US/docs/Web/API/Fullscreen_API
			// see https://msdn.microsoft.com/library/dn265028
			//          is enabled          request function    exit function      current element
			'': ['fullscreenEnabled', 'requestFullscreen', 'exitFullscreen', 'fullscreenElement'], // Edge
			moz: ['FullScreenEnabled', 'RequestFullScreen', 'CancelFullScreen', 'FullScreenElement'], // All Firefox
			webkit: ['FullscreenEnabled', 'RequestFullscreen', 'ExitFullscreen', 'FullscreenElement'], // All Webkit
			ms: ['FullscreenEnabled', 'RequestFullscreen', 'ExitFullscreen', 'FullscreenElement'], // IE 11+
		};
		/**
		 * The prefixed versions of the property names
		 * @type {Object}
		 */
		this.prefixes = {};
		_.some(specs, (keys, prefix) => {
			// e.g. document.documentElement.requestFullscreen
			if (document.documentElement[prefix + keys[1]]) {
				this.prefixes = {
					fullscreenEnabled: prefix + keys[0],
					requestFullscreen: prefix + keys[1],
					exitFullscreen: prefix + keys[2],
					fullscreenElement: prefix + keys[3],
					events: {
						change: prefix + 'fullscreenchange',
						error: prefix + 'fullscreenerror',
					},
				};
				return true;
			}
		});
	}

	/**
	 * Decide if full screen is enabled in this browser
	 * @returns {Boolean}
	 */
	enabled() {
		// standard: document.fullscreenEnabled
		return this.prefixes && document[this.prefixes.fullscreenEnabled];
	}

	/**
	 * Attempt to enter full screen
	 * @param {Element} [element=document.documentElement]  The element to make full screen (or document.documentElement for the entire screen)
	 * @returns {FullscreenService}
	 */
	request(element) {
		if (!this.enabled()) {
			console.warn('Full screen is not enabled in this browser');
			return this;
		}
		// Safari alone needs Element.ALLOW_KEYBOARD_INPUT
		// In other browsers it will be undefined which is ok to pass
		// standard: element.requestFullscreen()
		(element || document.documentElement)[this.prefixes.requestFullscreen](Element.ALLOW_KEYBOARD_INPUT);
		return this;
	}

	/**
	 * Leave full screen
	 * @returns {FullscreenService}
	 */
	exit() {
		if (!this.enabled()) {
			console.warn('Full screen is not enabled in this browser');
			return this;
		}
		// standard: document.exitFullscreen()
		document[this.prefixes.exitFullscreen]();
		return this;
	}

	/**
	 * Get the element that is currently full screen or undefined if there isn't one
	 * @returns {Element|undefined}
	 */
	element() {
		// standard: document.fullscreenElement
		return document[this.prefixes.fullscreenElement];
	}

	/**
	 * Attach a callback to fire whenever a fullscreen event is triggered on the document
	 * @param {String} type  Either fullscreenchange or fullscreenerror
	 * @param {Function} handler  The event listener to trigger
	 * @returns {FullscreenService}
	 */
	on(type, handler) {
		// standard: fullscreenchange and fullscreenerror
		type = type.replace('fullscreen', '');
		document.addEventListener(this.prefixes.events[type], handler, false);
		return this;
	}

	/**
	 * Remove an attached callback
	 * @param {String} type  Either fullscreenchange or fullscreenerror
	 * @param {Function} handler  The event listener
	 * @returns {FullscreenService}
	 */
	off(type, handler) {
		// standard: fullscreenchange and fullscreenerror
		type = type.replace('fullscreen', '');
		document.removeEventListener(this.prefixes.events[type], handler);
		return this;
	}

	/**
	 * Attach a callback to fire THE FIRST TIME a fullscreen event is triggered on the document
	 * @param {String} type  Either fullscreenchange or fullscreenerror
	 * @param {Function} handler  The event listener to trigger
	 * @returns {FullscreenService}
	 */
	one(type, handler) {
		let svc = this;
		this.on(type, function (evt) {
			svc.off(type, handler);
			handler.call(this, evt);
		});
		return this;
	}
}
